import {
  Bell,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Eye,
  FilePlus,
  FileText,
  Filter,
  Flame,
  Home,
  MessageSquare,
  Pencil,
  Snowflake,
  Thermometer,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { AddLeadDialog } from '@/modules/marketing/components/leads/AddLeadDialog'
import { triggerPdfDownload } from '@/modules/sales/api/quotation.api'
import LeadCommentsModal from '@/modules/sales/components/leads/LeadCommentsModal'
import LeadStatusDropdown from '@/modules/sales/components/leads/LeadStatusDropdown'
import { useCommentsStore } from '@/modules/sales/stores/comments.store'
import { DataTable } from '@/shared/components/data-table'
import { ExpandableText } from '@/shared/components/ExpandableText'
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { ViewProductsModal } from '@/shared/components/ViewProductsModal'
import { toast } from '@/shared/lib/toast'
import { cn } from '@/shared/lib/utils'

import {
  getAdminQuotationById,
  getAdminQuotationByLeadId,
  exportAdminQuotation,
} from '../api/quotation.api'
import AssignSalesDropdown from '../components/leads/AssignSalesDropdown'
import ReassignSalesDropdown from '../components/leads/ReassignSalesDropdown'
import { QuoteDetailsModal } from '../components/quotations'
import { useLeads } from '../hooks/useLeads'
import { useSales } from '../hooks/useSales'

function LinkedInIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden
    >
      <path d="M6.94 6.5A1.94 1.94 0 1 1 5 4.56 1.94 1.94 0 0 1 6.94 6.5ZM7 8.75H5v10.5h2V8.75Zm4.25 0H9.25v10.5h2v-5.5c0-1.45.83-2.25 2.05-2.25 1.12 0 1.7.75 1.7 2.25v5.5H17v-5.9c0-3.05-1.63-4.45-3.8-4.45-1.75 0-2.52.97-2.95 1.65V8.75Z" />
    </svg>
  )
}

function FacebookIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden
    >
      <path d="M14.5 8.25h1.75V5.5H14.4c-2.2 0-3.65 1.35-3.65 3.7v1.55H8.75v2.75h2v7.75h3.1v-7.75h2.2l.45-2.75h-2.65V9.4c0-.7.3-1.15 1.15-1.15Z" />
    </svg>
  )
}

const SOURCE_STYLES = {
  linkedin: {
    label: 'LinkedIn',
    className: 'border-sky-200 bg-sky-50 text-sky-700',
    icon: LinkedInIcon,
  },
  facebook: {
    label: 'Facebook',
    className: 'border-blue-200 bg-blue-50 text-blue-700',
    icon: FacebookIcon,
  },
  offline: {
    label: 'Offline',
    className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    icon: Home,
  },
}

const PAGE_SIZE = 5

const LEAD_ORIGIN_OPTIONS = [
  { value: 'all', label: 'All Origins' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'sales', label: 'Sales' },
  { value: 'admin', label: 'Admin' },
]

const LEAD_STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
]

const LEAD_PERIOD_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'previous_month', label: 'Previous Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'previous_quarter', label: 'Previous Quarter' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom Date Range' },
]

function SourceBadge({ source }) {
  const config = SOURCE_STYLES[source] || SOURCE_STYLES.offline
  const Icon = config.icon

  return (
    <Badge
      className={cn(
        'gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold',
        config.className,
      )}
    >
      {Icon ? <Icon className="size-3" /> : null}
      {config.label}
    </Badge>
  )
}

export function AdminLeadsPage({ source, status = '', leadWarmth = '' }) {
  const _navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const originFilter = searchParams.get('origin') || 'all'
  const [isOriginOpen, setIsOriginOpen] = useState(false)

  const salesPersonFilter = searchParams.get('salesPerson') || 'all'
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const statusFilter = searchParams.get('status') || status || 'all'
  const [isStatusOpen, setIsStatusOpen] = useState(false)

  const period = searchParams.get('period') || 'all'
  const appliedStartDate = searchParams.get('startDate') || ''
  const appliedEndDate = searchParams.get('endDate') || ''
  const [isPeriodOpen, setIsPeriodOpen] = useState(false)
  const [startDate, setStartDate] = useState(appliedStartDate)
  const [endDate, setEndDate] = useState(appliedEndDate)

  const hasDrilldownFilter = Boolean(
    (statusFilter && statusFilter !== 'all') ||
    leadWarmth ||
    (source && source !== 'all') ||
    (originFilter && originFilter !== 'all') ||
    (salesPersonFilter && salesPersonFilter !== 'all') ||
    (period && period !== 'all'),
  )

  const handleOriginChange = (val) => {
    setPage(1)
    setIsOriginOpen(false)
    const updated = new URLSearchParams(searchParams)
    if (val === 'all') {
      updated.delete('origin')
    } else {
      updated.set('origin', val)
    }
    setSearchParams(updated)
  }

  const handleSalesPersonChange = (val) => {
    setPage(1)
    setIsFilterOpen(false)
    const updated = new URLSearchParams(searchParams)
    if (val === 'all') {
      updated.delete('salesPerson')
    } else {
      updated.set('salesPerson', val)
    }
    setSearchParams(updated)
  }

  const handleStatusChange = (val) => {
    setPage(1)
    setIsStatusOpen(false)
    const updated = new URLSearchParams(searchParams)
    if (val === 'all') {
      updated.delete('status')
    } else {
      updated.set('status', val)
    }
    setSearchParams(updated)
  }

  const clearFilter = (key) => {
    if (key === 'period') {
      setStartDate('')
      setEndDate('')
    }
    const updated = new URLSearchParams(searchParams)
    updated.delete(key)
    if (key === 'period') {
      updated.delete('startDate')
      updated.delete('endDate')
    }
    setPage(1)
    setSearchParams(updated)
  }

  const clearAllFilters = () => {
    setStartDate('')
    setEndDate('')
    const updated = new URLSearchParams(searchParams)
    updated.delete('status')
    updated.delete('leadWarmth')
    updated.delete('source')
    updated.delete('salesPerson')
    updated.delete('origin')
    updated.delete('period')
    updated.delete('startDate')
    updated.delete('endDate')
    setPage(1)
    setSearchParams(updated)
  }

  const [isCommentsOpen, setIsCommentsOpen] = useState(false)
  const [selectedLead, setSelectedLead] = useState(null)
  const [selectedLeadForEdit, setSelectedLeadForEdit] = useState(null)
  const [isEditLeadOpen, setIsEditLeadOpen] = useState(false)
  const [viewProductsLead, setViewProductsLead] = useState(null)
  const [selectedQuoteForModal, setSelectedQuoteForModal] = useState(null)
  const [isQuoteDetailsOpen, setIsQuoteDetailsOpen] = useState(false)

  // Follow-up loading state is handled locally.
  const [followUpLoading, setFollowUpLoading] = useState({})

  // Immediate synchronous lock to prevent duplicate API calls.
  const followUpLoadingRef = useRef(new Set())

  const {
    leads,
    meta,
    isLoading,
    fetchLeads,
    assignLead,
    sendFollowUpEmail,
    updateLeadStatus,
  } = useLeads()

  const { sales, fetchSales } = useSales()

  const notifications = useCommentsStore((state) => state.notifications)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 300)

    return () => window.clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    if (period === 'custom' && (!appliedStartDate || !appliedEndDate)) {
      return
    }

    fetchLeads({
      page,
      limit: PAGE_SIZE,
      search,
      source: source === 'all' ? undefined : source,
      origin: originFilter === 'all' ? undefined : originFilter,
      salesPerson: salesPersonFilter !== 'all' ? salesPersonFilter : undefined,
      period: period === 'all' ? undefined : period,
      startDate: period === 'custom' ? appliedStartDate : undefined,
      endDate: period === 'custom' ? appliedEndDate : undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      leadWarmth: leadWarmth === 'all' ? undefined : leadWarmth,
    })
  }, [
    fetchLeads,
    page,
    search,
    source,
    originFilter,
    salesPersonFilter,
    statusFilter,
    period,
    appliedStartDate,
    appliedEndDate,
    leadWarmth,
  ])

  useEffect(() => {
    const handleGlobalUpdate = () => {
      if (period === 'custom' && (!appliedStartDate || !appliedEndDate)) {
        return
      }

      fetchLeads({
        page,
        limit: PAGE_SIZE,
        search,
        source: source === 'all' ? undefined : source,
        origin: originFilter === 'all' ? undefined : originFilter,
        salesPerson:
          salesPersonFilter !== 'all' ? salesPersonFilter : undefined,
        period: period === 'all' ? undefined : period,
        startDate: period === 'custom' ? appliedStartDate : undefined,
        endDate: period === 'custom' ? appliedEndDate : undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        leadWarmth: leadWarmth === 'all' ? undefined : leadWarmth,
      })
    }

    window.addEventListener('crm:data-changed', handleGlobalUpdate)
    window.addEventListener('focus', handleGlobalUpdate)

    return () => {
      window.removeEventListener('crm:data-changed', handleGlobalUpdate)
      window.removeEventListener('focus', handleGlobalUpdate)
    }
  }, [
    fetchLeads,
    page,
    search,
    source,
    originFilter,
    salesPersonFilter,
    statusFilter,
    period,
    appliedStartDate,
    appliedEndDate,
    leadWarmth,
  ])

  const handlePeriodChange = (val) => {
    setPage(1)
    setIsPeriodOpen(false)
    const updated = new URLSearchParams(searchParams)
    if (val === 'all') {
      updated.delete('period')
    } else {
      updated.set('period', val)
    }
    if (val !== 'custom') {
      setStartDate('')
      setEndDate('')
      updated.delete('startDate')
      updated.delete('endDate')
    }
    setSearchParams(updated)
  }

  const handleApplyCustomDate = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates')
      return
    }
    if (startDate > endDate) {
      toast.error('Start date cannot be after end date')
      return
    }
    setPage(1)
    const updated = new URLSearchParams(searchParams)
    updated.set('period', 'custom')
    updated.set('startDate', startDate)
    updated.set('endDate', endDate)
    setSearchParams(updated)
  }

  useEffect(() => {
    fetchSales({
      page: 1,
      limit: 100,
    })
  }, [fetchSales])

  const getUnreadCountForLead = (leadId) =>
    notifications.filter(
      (notification) => notification.leadId === leadId && !notification.isRead,
    ).length

  const handleFollowUp = async (leadId) => {
    if (!leadId) return

    const id = String(leadId)

    // Prevent another request for the same lead immediately.
    if (followUpLoadingRef.current.has(id)) {
      return
    }

    // Lock immediately before starting the API call.
    followUpLoadingRef.current.add(id)

    setFollowUpLoading((prev) => ({
      ...prev,
      [id]: true,
    }))

    try {
      // Keep loading until this complete promise resolves/rejects.
      await sendFollowUpEmail(leadId)
    } catch (error) {
      console.error(error)
    } finally {
      // Unlock only after the complete API call finishes.
      followUpLoadingRef.current.delete(id)

      setFollowUpLoading((prev) => ({
        ...prev,
        [id]: false,
      }))
    }
  }

  const handleViewQuote = async (row) => {
    try {
      if (row.quotation?._id) {
        const res = await getAdminQuotationById(row.quotation._id)
        setSelectedQuoteForModal(res?.data || res || row.quotation)
      } else {
        const res = await getAdminQuotationByLeadId(row._id)
        setSelectedQuoteForModal(res?.data || res)
      }
      setIsQuoteDetailsOpen(true)
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to load quotation details',
      )
    }
  }

  const columns = [
    {
      id: 'lead',
      header: 'Company',
      cell: (row) => (
        <div className="flex min-w-[200px] items-center gap-3">
          <Avatar className="size-9">
            <AvatarFallback className="bg-muted text-xs font-bold text-foreground">
              {(row.company || row.name || '')
                .split(' ')
                .map((part) => part[0])
                .join('')
                .slice(0, 2)
                .toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            {/* Company Name */}
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-bold text-foreground">
                {row.company || '-'}
              </p>

              {row.leadWarmth === 'hot' && (
                <Flame className="size-3.5 text-orange-500" />
              )}

              {row.leadWarmth === 'cold' && (
                <Snowflake className="size-3.5 text-sky-500" />
              )}

              {row.leadWarmth === 'warm' && (
                <Thermometer className="size-3.5 text-amber-500" />
              )}
            </div>
          </div>
        </div>
      ),
    },

    {
      id: 'createdAt',
      header: 'Date',
      cell: (row) => {
        if (!row.createdAt)
          return <span className="text-muted-foreground">-</span>
        const d = new Date(row.createdAt)
        return (
          <span className="whitespace-nowrap text-xs font-medium text-slate-600">
            {d.toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </span>
        )
      },
    },

    {
      id: 'source',
      header: 'Source',
      cell: (row) => <SourceBadge source={row.source} />,
    },
    {
      id: 'leadOrigin',
      header: 'Lead Origin',
      cell: (row) => {
        const role = row.createdBy?.role?.toLowerCase()

        if (role === 'marketing') {
          return (
            <Badge className="rounded-full border border-purple-200 bg-purple-50 px-2.5 py-1 text-[11px] font-semibold text-purple-700">
              M
            </Badge>
          )
        }

        if (role === 'sales') {
          return (
            <Badge className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700">
              S
            </Badge>
          )
        }

        if (role === 'admin') {
          return (
            <Badge className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
              A
            </Badge>
          )
        }

        return <span className="text-muted-foreground">-</span>
      },
    },
    {
      id: 'product',
      header: 'Product',
      cell: (row) => {
        const products = row.products || []

        if (!products.length) {
          return <span className="text-muted-foreground">-</span>
        }

        const productsWithDesc = products.filter(
          (p) =>
            typeof p.description === 'string' &&
            p.description.trim().length > 0,
        )
        const productsWithoutDesc = products.filter(
          (p) =>
            !p.description ||
            typeof p.description !== 'string' ||
            p.description.trim().length === 0,
        )
        const isPartialDesc =
          products.length > 1 &&
          productsWithDesc.length > 0 &&
          productsWithoutDesc.length > 0

        const firstProduct = isPartialDesc
          ? productsWithDesc[0]?.product || '-'
          : products[0].product
        const restCount = products.length - 1

        return (
          <div className="flex items-center gap-1.5 max-w-[200px]">
            <ExpandableText
              text={firstProduct}
              maxLength={20}
              expandable={false}
            />

            {restCount > 0 && (
              <button
                type="button"
                onClick={() => setViewProductsLead(row)}
                className="inline-flex shrink-0 items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-600 transition-colors hover:bg-indigo-100 cursor-pointer"
              >
                +{restCount} more
              </button>
            )}
          </div>
        )
      },
    },
    {
      id: 'description',
      header: 'Description',
      cell: (row) => {
        const products = row.products || []
        const productsWithDesc = products.filter(
          (p) =>
            typeof p.description === 'string' &&
            p.description.trim().length > 0,
        )
        const productsWithoutDesc = products.filter(
          (p) =>
            !p.description ||
            typeof p.description !== 'string' ||
            p.description.trim().length === 0,
        )
        const isPartialDesc =
          products.length > 1 &&
          productsWithDesc.length > 0 &&
          productsWithoutDesc.length > 0

        const sourceList = isPartialDesc ? productsWithDesc : products
        const descriptions = sourceList
          .map((p) =>
            typeof p.description === 'string' ? p.description.trim() : '',
          )
          .filter(Boolean)

        if (!descriptions.length && row.description?.trim()) {
          descriptions.push(row.description.trim())
        }
        if (!descriptions.length)
          return <span className="text-muted-foreground">-</span>

        const firstDesc = descriptions[0]
        const restDescCount = descriptions.length - 1

        return (
          <div className="flex items-center gap-1.5 max-w-[200px]">
            <ExpandableText
              text={firstDesc}
              maxLength={20}
              expandable={false}
            />
            {restDescCount > 0 && (
              <button
                type="button"
                onClick={() => setViewProductsLead(row)}
                className="inline-flex shrink-0 items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-600 transition-colors hover:bg-indigo-100 cursor-pointer"
              >
                +{restDescCount} more
              </button>
            )}
          </div>
        )
      },
    },

    {
      id: 'quantity',
      header: 'Quantity',
      headerClassName: 'text-center',
      className: 'text-center',
      cell: (row) => {
        const products = row.products || []

        if (!products.length) {
          return <span className="text-muted-foreground">-</span>
        }

        const totalQty = products.reduce(
          (sum, item) => sum + (Number(item.quantity) || 0),
          0,
        )

        return (
          <span className="text-sm font-semibold text-foreground">
            {totalQty}
          </span>
        )
      },
    },

    {
      id: 'value',
      header: 'Value',
      headerClassName: 'text-center',
      className: 'text-center',
      cell: (row) => {
        const products = row.products || []

        if (!products.length) {
          return <span className="text-muted-foreground">-</span>
        }

        const totalValue = products.reduce(
          (sum, item) =>
            sum + (Number(item.quantity) || 0) * (Number(item.value) || 0),
          0,
        )

        return (
          <span className="text-sm font-semibold text-foreground">
            ₹{totalValue.toLocaleString('en-IN')}
          </span>
        )
      },
    },

    {
      id: 'salesPerson',
      header: 'Sales Person',
      cell: (row) => (
        <span className="text-sm font-medium">
          {row.salesPerson?.name || row.createdBy?.name || '-'}
        </span>
      ),
    },

    {
      id: 'status',
      header: 'Status',
      cell: (row) => {
        const leadId = row._id || row.id
        return (
          <LeadStatusDropdown
            value={row.status || 'open'}
            onChange={async (value) => {
              try {
                await updateLeadStatus(leadId, value)
              } catch (error) {
                console.error(error)
              }
            }}
          />
        )
      },
    },

    {
      id: 'leadWarmth',
      header: 'Lead Warmth',
      cell: (row) => <Badge className="capitalize">{row.leadWarmth}</Badge>,
    },

    {
      id: 'quotationStatus',
      header: 'Quotation Status',
      cell: (row) => {
        const quote = row.quotation
        if (quote?.isSent || quote?.status === 'sent') {
          return (
            <Badge
              className="gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 cursor-pointer hover:bg-emerald-100 hover:border-emerald-300 transition-colors"
              title={
                quote.quoteNo
                  ? `Click to view Quote #${quote.quoteNo}`
                  : 'Click to view Quotation'
              }
              onClick={() => handleViewQuote(row)}
            >
              <span className="size-1.5 rounded-full bg-emerald-500" />
              Sent
              {quote.quoteNo ? ` (${quote.quoteNo})` : ''}
            </Badge>
          )
        }
        return (
          <Badge className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] font-medium text-slate-500">
            Not Sent
          </Badge>
        )
      },
    },

    {
      id: 'actions',
      header: 'Actions',
      cell: (row) => {
        const leadId = String(row._id)
        const isFollowUpLoading = !!followUpLoading[leadId]

        return (
          <div className="flex min-w-[780px] items-center gap-2">
            {/* View */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 rounded-full border-indigo-200 bg-indigo-50/80 px-3 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
              onClick={() => setViewProductsLead(row)}
            >
              <Eye className="size-3.5" />
            </Button>

            {/* Edit */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 rounded-full border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              onClick={() => {
                setSelectedLeadForEdit(row)
                setIsEditLeadOpen(true)
              }}
            >
              <Pencil className="size-3.5" />
            </Button>

            {/* Create Quotation */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 rounded-full border-blue-200 bg-blue-50/80 px-3 text-xs font-semibold text-blue-700 hover:bg-blue-100"
              title="Create / Revise Quotation"
              onClick={() => {
                _navigate(`/admin/leads/create-quotation/${row._id}`, {
                  state: {
                    lead: row,
                    from: 'leads',
                  },
                })
              }}
            >
              <FilePlus className="size-3.5" />
            </Button>

            {/* Assign */}
            <div className="w-[130px]">
              <AssignSalesDropdown
                sales={sales}
                selectedSalesId={row.salesPerson?._id}
                disabled={!!row.salesPerson}
                onAssign={async (person) => {
                  await assignLead(row._id, person._id)
                }}
              />
            </div>

            {/* Reassign */}
            <div className="w-[130px]">
              <ReassignSalesDropdown
                sales={sales}
                currentSalesPersonId={row.salesPerson?._id}
                onReassign={async (person) => {
                  await assignLead(row._id, person._id)
                }}
              />
            </div>

            {/* Follow Up */}
            <div className="w-[130px]">
              {row.followUpConfirmed === true ? (
                <span className="inline-flex h-8 w-full items-center justify-center gap-1 rounded-full bg-emerald-100 px-2.5 text-[11px] font-semibold text-emerald-700">
                  <Bell className="size-3.5" />
                  Confirmed ✓
                </span>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  disabled={isFollowUpLoading || !row.salesPerson}
                  title={
                    !row.salesPerson ? 'Assign salesperson first' : undefined
                  }
                  className="h-8 w-full gap-1 rounded-full bg-emerald-600 px-2.5 text-[11px] text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={() => handleFollowUp(row._id)}
                >
                  <Bell className="size-3.5" />

                  {isFollowUpLoading
                    ? 'Sending...'
                    : !row.salesPerson
                      ? 'Assign first'
                      : row.followUpSent
                        ? 'Follow up again'
                        : 'Follow up'}

                  {!isFollowUpLoading && <ChevronDown className="size-3" />}
                </Button>
              )}
            </div>

            {/* Comments */}
            <div className="relative shrink-0">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full text-muted-foreground"
                aria-label="Message"
                onClick={() => {
                  setSelectedLead(row)
                  setIsCommentsOpen(true)
                }}
              >
                <MessageSquare className="size-4" />

                {getUnreadCountForLead(row._id) > 0 && (
                  <span className="absolute -right-1 -top-1 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {getUnreadCountForLead(row._id) > 99
                      ? '99+'
                      : getUnreadCountForLead(row._id)}
                  </span>
                )}
              </Button>
            </div>

            {/* Download quotation */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full text-muted-foreground"
              aria-label="Download Quotation PDF"
              title="Download Quotation PDF"
              onClick={async () => {
                try {
                  const res = await getAdminQuotationByLeadId(row._id)
                  const quote = res?.data

                  if (quote) {
                    if (quote.quotationPdf || quote.quatationpdf) {
                      triggerPdfDownload(
                        quote.quotationPdf || quote.quatationpdf,
                        `quotation-${quote.quoteNo || row.name}.pdf`,
                      )
                    } else {
                      const blob = await exportAdminQuotation(quote._id)

                      triggerPdfDownload(
                        blob,
                        `quotation-${quote.quoteNo || row.name}.pdf`,
                      )
                    }

                    toast.success('Downloading quotation PDF...')
                  } else {
                    toast.error('No quotation found for this lead')
                  }
                } catch (error) {
                  toast.error(
                    error?.response?.data?.message ||
                      error?.message ||
                      'No quotation created for this lead yet',
                  )
                }
              }}
            >
              <FileText className="size-4" />
            </Button>
          </div>
        )
      },
    },
  ]

  return (
    <>
      {hasDrilldownFilter && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-xs">
          <span className="text-xs font-semibold text-slate-500">
            Filtered by:
          </span>
          {statusFilter && statusFilter !== 'all' && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
              Status:{' '}
              {LEAD_STATUS_OPTIONS.find((opt) => opt.value === statusFilter)
                ?.label || statusFilter.toUpperCase().replace(/_/g, ' ')}
              <button
                type="button"
                onClick={() => clearFilter('status')}
                className="hover:text-blue-950"
              >
                <X className="size-3" />
              </button>
            </span>
          )}
          {leadWarmth && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
              Warmth: {leadWarmth.toUpperCase()}
              <button
                type="button"
                onClick={() => clearFilter('leadWarmth')}
                className="hover:text-amber-950"
              >
                <X className="size-3" />
              </button>
            </span>
          )}
          {source && source !== 'all' && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-700">
              Source: {source.toUpperCase()}
              <button
                type="button"
                onClick={() => clearFilter('source')}
                className="hover:text-purple-950"
              >
                <X className="size-3" />
              </button>
            </span>
          )}
          {originFilter && originFilter !== 'all' && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700">
              Origin:{' '}
              {LEAD_ORIGIN_OPTIONS.find((opt) => opt.value === originFilter)
                ?.label || originFilter}
              <button
                type="button"
                onClick={() => clearFilter('origin')}
                className="hover:text-cyan-950"
              >
                <X className="size-3" />
              </button>
            </span>
          )}
          {salesPersonFilter && salesPersonFilter !== 'all' && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              Salesperson:{' '}
              {sales.find((s) => s._id === salesPersonFilter)?.name ||
                'Selected'}
              <button
                type="button"
                onClick={() => clearFilter('salesPerson')}
                className="hover:text-emerald-950"
              >
                <X className="size-3" />
              </button>
            </span>
          )}
          {period && period !== 'all' && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
              Period:{' '}
              {LEAD_PERIOD_OPTIONS.find((opt) => opt.value === period)?.label ||
                period}
              <button
                type="button"
                onClick={() => clearFilter('period')}
                className="hover:text-indigo-950"
              >
                <X className="size-3" />
              </button>
            </span>
          )}
          <button
            type="button"
            onClick={clearAllFilters}
            className="text-xs font-medium text-slate-500 underline hover:text-slate-800"
          >
            Clear all filters
          </button>
        </div>
      )}

      <DataTable
        columns={columns}
        data={leads}
        loading={isLoading}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          placeholder: 'Search leads by name, company, phone...',
        }}
        filterSummary={`${meta?.total || 0} Leads`}
        toolbarExtra={
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            {/* Lead Origin filter */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setIsOriginOpen((prev) => !prev)
                  setIsFilterOpen(false)
                  setIsStatusOpen(false)
                  setIsPeriodOpen(false)
                }}
                className="flex h-9 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
              >
                <Filter className="size-3.5 text-slate-500" />
                {LEAD_ORIGIN_OPTIONS.find((opt) => opt.value === originFilter)
                  ?.label || 'All Origins'}
                {isOriginOpen ? (
                  <ChevronUp className="size-3 text-slate-400" />
                ) : (
                  <ChevronDown className="size-3 text-slate-400" />
                )}
              </button>

              {isOriginOpen && (
                <div className="absolute right-0 top-full z-50 mt-1 w-44 rounded-xl border border-slate-100 bg-white py-1.5 shadow-xl">
                  {LEAD_ORIGIN_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`w-full px-4 py-2 text-left text-xs font-medium hover:bg-slate-50 ${
                        originFilter === opt.value
                          ? 'text-indigo-600 font-semibold'
                          : 'text-slate-700'
                      }`}
                      onClick={() => handleOriginChange(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Sales Person filter */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setIsFilterOpen((prev) => !prev)
                  setIsOriginOpen(false)
                  setIsStatusOpen(false)
                  setIsPeriodOpen(false)
                }}
                className="flex h-9 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
              >
                <Filter className="size-3.5 text-slate-500" />
                {salesPersonFilter === 'all'
                  ? 'All Sales Persons'
                  : (sales.find((s) => s._id === salesPersonFilter)?.name ??
                    'Sales Person')}
                {isFilterOpen ? (
                  <ChevronUp className="size-3 text-slate-400" />
                ) : (
                  <ChevronDown className="size-3 text-slate-400" />
                )}
              </button>

              {isFilterOpen && (
                <div className="absolute right-0 top-full z-50 mt-1 max-h-60 w-52 overflow-y-auto rounded-xl border border-slate-100 bg-white py-1.5 shadow-xl">
                  <button
                    type="button"
                    className={`w-full px-4 py-2 text-left text-xs font-medium hover:bg-slate-50 ${
                      salesPersonFilter === 'all'
                        ? 'text-indigo-600 font-semibold'
                        : 'text-slate-700'
                    }`}
                    onClick={() => handleSalesPersonChange('all')}
                  >
                    All Sales Persons
                  </button>

                  {sales.map((person) => (
                    <button
                      key={person._id}
                      type="button"
                      className={`w-full px-4 py-2 text-left text-xs font-medium hover:bg-slate-50 ${
                        salesPersonFilter === person._id
                          ? 'text-indigo-600 font-semibold'
                          : 'text-slate-700'
                      }`}
                      onClick={() => handleSalesPersonChange(person._id)}
                    >
                      {person.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Lead Status filter */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setIsStatusOpen((prev) => !prev)
                  setIsOriginOpen(false)
                  setIsFilterOpen(false)
                  setIsPeriodOpen(false)
                }}
                className="flex h-9 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
              >
                <Filter className="size-3.5 text-slate-500" />
                {LEAD_STATUS_OPTIONS.find((opt) => opt.value === statusFilter)
                  ?.label || 'All Statuses'}
                {isStatusOpen ? (
                  <ChevronUp className="size-3 text-slate-400" />
                ) : (
                  <ChevronDown className="size-3 text-slate-400" />
                )}
              </button>

              {isStatusOpen && (
                <div className="absolute right-0 top-full z-50 mt-1 w-44 rounded-xl border border-slate-100 bg-white py-1.5 shadow-xl">
                  {LEAD_STATUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`w-full px-4 py-2 text-left text-xs font-medium hover:bg-slate-50 ${
                        statusFilter === opt.value
                          ? 'text-indigo-600 font-semibold'
                          : 'text-slate-700'
                      }`}
                      onClick={() => handleStatusChange(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Duration / Period Filter */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setIsPeriodOpen((prev) => !prev)
                  setIsFilterOpen(false)
                  setIsOriginOpen(false)
                  setIsStatusOpen(false)
                }}
                className="flex h-9 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
              >
                <CalendarDays className="size-3.5 text-slate-500" />
                {LEAD_PERIOD_OPTIONS.find((opt) => opt.value === period)
                  ?.label || 'All Time'}
                {isPeriodOpen ? (
                  <ChevronUp className="size-3 text-slate-400" />
                ) : (
                  <ChevronDown className="size-3 text-slate-400" />
                )}
              </button>

              {isPeriodOpen && (
                <div className="absolute right-0 top-full z-50 mt-1 w-52 rounded-xl border border-slate-100 bg-white py-1.5 shadow-xl">
                  {LEAD_PERIOD_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`w-full px-4 py-2 text-left text-xs font-medium hover:bg-slate-50 ${
                        period === opt.value
                          ? 'text-indigo-600 font-semibold'
                          : 'text-slate-700'
                      }`}
                      onClick={() => handlePeriodChange(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Custom Date Range inputs when Custom is selected */}
            {period === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-9 rounded-full border border-slate-200 bg-white px-3 text-xs outline-none focus:border-indigo-400"
                />
                <span className="text-xs text-slate-400">-</span>
                <input
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-9 rounded-full border border-slate-200 bg-white px-3 text-xs outline-none focus:border-indigo-400"
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={!startDate || !endDate}
                  onClick={handleApplyCustomDate}
                  className="h-9 rounded-full bg-[#0F172A] px-4 text-xs text-white hover:bg-slate-800"
                >
                  Apply
                </Button>
              </div>
            )}
          </div>
        }
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          total: meta?.total || 0,
          onPageChange: setPage,
        }}
        emptyMessage="No leads match your search."
      />

      <LeadCommentsModal
        open={isCommentsOpen}
        onOpenChange={setIsCommentsOpen}
        lead={selectedLead}
        conversationWith={selectedLead?.name}
      />

      <ViewProductsModal
        open={!!viewProductsLead}
        onOpenChange={(open) => !open && setViewProductsLead(null)}
        lead={viewProductsLead}
      />

      <AddLeadDialog
        open={isEditLeadOpen}
        onOpenChange={(open) => {
          setIsEditLeadOpen(open)
          if (!open) setSelectedLeadForEdit(null)
        }}
        lead={selectedLeadForEdit}
        module="admin"
        onSuccess={() => {
          fetchLeads({
            page,
            limit: PAGE_SIZE,
            search,
            source: source === 'all' ? undefined : source,
            origin: originFilter === 'all' ? undefined : originFilter,
            salesPerson:
              salesPersonFilter !== 'all' ? salesPersonFilter : undefined,
            period: period === 'all' ? undefined : period,
            startDate: period === 'custom' ? appliedStartDate : undefined,
            endDate: period === 'custom' ? appliedEndDate : undefined,
          })
        }}
      />

      <QuoteDetailsModal
        open={isQuoteDetailsOpen}
        onOpenChange={setIsQuoteDetailsOpen}
        quote={selectedQuoteForModal}
        isAdmin
      />
    </>
  )
}

export default AdminLeadsPage
